import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';

/**
 * The org-level pages that host `<mtm-drive-comments-panel>`, in ONE place.
 *
 * An org page anchors the drive feed on its own entity's uuid (brand / workspace /
 * campaign directories are uuid-pinned by the backend hierarchy materialization), so
 * three facts about such a page have to agree, and used to live apart:
 *
 *  - which URLs are org pages — child-layout's `isOrgDrivePage` gate,
 *  - which `PLATFORM_CHAT_ENTITY_TYPE` the page passes to the panel,
 *  - how to build the page's URL from an anchor uuid — what a Recent activity row
 *    needs to navigate BACK to a draft left on one of these pages.
 *
 * The third is why this file exists. A draft row carries only `elementUuid` +
 * `anchorKind: 'DIRECTORY'` and `projectUuid: null`, which is indistinguishable from a
 * My Own Space draft, so the recent-activity router used to send every project-less row
 * to `/my-space`. Resolving the anchor's drive kind against this table is what turns the
 * uuid back into a page.
 *
 * **Adding a page:** append one descriptor. Nothing else needs to know about it.
 *
 * The anchor's URL must be derivable from the anchor uuid plus its drive ancestors —
 * that is the whole constraint. A campaign needs its workspace (`wid`), which is its
 * `WORKSPACE` ancestor. A page needing an id that is not in the drive hierarchy cannot
 * be expressed here and would need the backend to put a `ctxId` on draft rows.
 */

/** Ids a route builder may draw on: the anchor itself plus its org ancestors. */
export interface OrgAnchorIds {
  /** The anchor the draft/feed hangs off — the org entity's own uuid. */
  readonly self: string;
  /** Uuid of the `BRAND` ancestor, when the chain had one. */
  readonly brand?: string;
  /** Uuid of the `WORKSPACE` ancestor, when the chain had one. */
  readonly workspace?: string;
}

/** A router target: `router.navigate(commands, { queryParams })`. */
export interface OrgEntityTarget {
  readonly commands: readonly string[];
  readonly queryParams: Record<string, string>;
}

/** `wid` / `dc` are AES-encrypted in the URL; the caller supplies `CryptoService.encryptString`. */
type Encrypt = (value: string) => string;

export interface OrgEntityPage {
  /** Platform entity type the page passes to `<mtm-drive-comments-panel>`. */
  readonly entityType: string;
  /**
   * Drive `directoryKind.directoryKindName` (or `DirectoryNode.elementType`) of the
   * anchor directory. Backend-seeded strings — the kind dictionary is the source of
   * truth, so these are matched case-insensitively with `-`/`_` folded together.
   */
  readonly driveKindNames: readonly string[];
  /** True when `url` is this page. Mirrors what the page's own route matches. */
  readonly matchesUrl: (url: string) => boolean;
  /** The page's address for an anchor, or null when the ids needed are missing. */
  readonly target: (ids: OrgAnchorIds, encrypt: Encrypt) => OrgEntityTarget | null;
}

export const ORG_ENTITY_PAGES: readonly OrgEntityPage[] = [
  {
    // The brand dashboard — `/workspaces` exactly (its children are other pages).
    entityType: PLATFORM_CHAT_ENTITY_TYPE.BRAND,
    driveKindNames: ['BRAND'],
    matchesUrl: (url: string): boolean => /^\/workspaces(\?|$)/.test(url),
    target: (): OrgEntityTarget => ({ commands: ['/workspaces'], queryParams: {} }),
  },
  {
    // A workspace's campaign list.
    entityType: PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE,
    driveKindNames: ['WORKSPACE'],
    matchesUrl: (url: string): boolean => url.includes('/workspaces/campaign-drive'),
    target: (ids: OrgAnchorIds, encrypt: Encrypt): OrgEntityTarget => ({
      commands: ['/workspaces', 'campaign-drive'],
      queryParams: { wid: encrypt(ids.self) },
    }),
  },
  {
    // A campaign's overview. Needs its workspace: the route carries both ids.
    entityType: PLATFORM_CHAT_ENTITY_TYPE.CAMPAIGN,
    driveKindNames: ['CAMPAIGN'],
    matchesUrl: (url: string): boolean => url.includes('/workspaces/campaigndrive/dashboard'),
    target: (ids: OrgAnchorIds, encrypt: Encrypt): OrgEntityTarget | null =>
      ids.workspace
        ? {
            commands: ['/workspaces', 'campaigndrive', 'dashboard'],
            queryParams: { wid: encrypt(ids.workspace), dc: encrypt(ids.self) },
          }
        : null,
  },
];

/** Fold the backend's kind spellings (`SUB-SECTION` vs `SUB_SECTION`, casing) into one key. */
function normalizeKindName(kindName: string): string {
  return kindName.toUpperCase().replace(/-/g, '_');
}

function indexByKind(pages: readonly OrgEntityPage[]): ReadonlyMap<string, OrgEntityPage> {
  const byKind: Map<string, OrgEntityPage> = new Map<string, OrgEntityPage>();
  for (const page of pages) {
    for (const kindName of page.driveKindNames) {
      byKind.set(normalizeKindName(kindName), page);
    }
  }
  return byKind;
}

const PAGES_BY_KIND: ReadonlyMap<string, OrgEntityPage> = indexByKind(ORG_ENTITY_PAGES);

/**
 * The org page owning a drive directory of `kindName`, or null when the kind is not an
 * org anchor (a project folder, a personal-space folder — every ordinary directory).
 */
export function orgEntityPageForKind(kindName: string | null | undefined): OrgEntityPage | null {
  if (!kindName) return null;
  return PAGES_BY_KIND.get(normalizeKindName(kindName)) ?? null;
}

/** The org page `url` is, or null when it is not one. */
export function orgEntityPageForUrl(url: string): OrgEntityPage | null {
  return ORG_ENTITY_PAGES.find((page: OrgEntityPage): boolean => page.matchesUrl(url)) ?? null;
}

/**
 * True when `url` is an org page hosting the drive comment shell. The gate child-layout
 * draws the shell and its right rail on.
 */
export function isOrgDriveUrl(url: string): boolean {
  return orgEntityPageForUrl(url) !== null;
}

/**
 * The brand page with no brand to anchor its feed on (an account in no brand). The brand
 * page only ever uses the drive feed, so it then hosts no comment or task panel at all.
 */
export function isBrandlessBrandPage(url: string, driveAnchorUuid: string | null | undefined): boolean {
  return orgEntityPageForUrl(url)?.entityType === PLATFORM_CHAT_ENTITY_TYPE.BRAND && !driveAnchorUuid;
}
